import { useEffect, useState } from 'react';
import { CalendarRange, CheckSquare2, NotebookPen, Plus, ShoppingBasket } from 'lucide-react';
import { EmptyState } from '../dashboard/EmptyState';
import type { Category, PlannerRecord, RecordKind } from '../../types';
import { RecordCard } from './RecordCard';
import { Pagination } from './Pagination';

type Props = {
  categories: readonly Category[];
  kind: RecordKind;
  isCompleted: (record: PlannerRecord) => boolean;
  onDelete: (record: PlannerRecord) => void;
  onEdit: (record: PlannerRecord) => void;
  onNew: () => void;
  onToggleChecklist: (record: PlannerRecord, itemId: string) => void;
  onToggleComplete: (record: PlannerRecord) => void;
  onToggleFavorite: (record: PlannerRecord) => void;
  onTogglePinned: (record: PlannerRecord) => void;
  records: readonly PlannerRecord[];
  timeFormat: 12 | 24;
};

const DETAILS = {
  task: { title: 'Tasks', singular: 'task', description: 'Plan priorities, dates and the next small step.', icon: CheckSquare2 },
  event: { title: 'Events', singular: 'event', description: 'Keep dates, times and places together.', icon: CalendarRange },
  note: { title: 'Notes', singular: 'note', description: 'Capture thoughts and lists without losing the thread.', icon: NotebookPen },
  shopping: { title: 'Shopping', singular: 'item', description: 'Collect what you need and check it off as you go.', icon: ShoppingBasket }
} as const;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const getRecordChronologicalKey = (record: PlannerRecord): { date: string; time: string; createdAt: string } => {
  const date = ('dueDate' in record && record.dueDate) ? record.dueDate : ('date' in record && record.date) ? record.date : '';
  const time = ('dueTime' in record && record.dueTime) ? record.dueTime : ('startTime' in record && record.startTime) ? record.startTime : '';
  const createdAt = record.createdAt || '';
  return { date, time, createdAt };
};

const compareRecordsChronological = (first: PlannerRecord, second: PlannerRecord): number => {
  const firstPinned = 'pinned' in first && first.pinned ? 1 : 0;
  const secondPinned = 'pinned' in second && second.pinned ? 1 : 0;
  if (firstPinned !== secondPinned) return secondPinned - firstPinned;

  const a = getRecordChronologicalKey(first);
  const b = getRecordChronologicalKey(second);

  // If both have dates, sort chronologically ascending (oldest date/time at the top)
  if (a.date && b.date) {
    const dateCmp = a.date.localeCompare(b.date);
    if (dateCmp !== 0) return dateCmp;
    const timeCmp = a.time.localeCompare(b.time);
    if (timeCmp !== 0) return timeCmp;
    return a.createdAt.localeCompare(b.createdAt);
  }

  // Prioritize dated items over undated items
  if (a.date && !b.date) return -1;
  if (!a.date && b.date) return 1;

  // For undated items (notes, shopping, tasks without date), sort chronologically by creation (oldest at the top)
  return a.createdAt.localeCompare(b.createdAt);
};

export const RecordsView = ({ categories, isCompleted, kind, onDelete, onEdit, onNew, onToggleChecklist, onToggleComplete, onToggleFavorite, onTogglePinned, records, timeFormat }: Props) => {
  const details = DETAILS[kind];
  const isMobile = useIsMobile();
  const itemsPerPage = isMobile ? 3 : 5;

  const [filter, setFilter] = useState<'all' | 'open' | 'completed'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const sorted = [...records]
    .filter((record) => filter === 'all' || (filter === 'completed' ? isCompleted(record) : !isCompleted(record)))
    .sort(compareRecordsChronological);

  const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  const handleFilter = (value: 'all' | 'open' | 'completed') => {
    setFilter(value);
    setCurrentPage(1);
  };

  const paginated = sorted.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  return (
    <div className="page-stack records-page-stack">
      <section className="module-header">
        <div><p className="eyebrow">Local planner</p><h2>{details.title}</h2><p>{details.description}</p></div>
        <button type="button" className="primary-button" onClick={onNew}><Plus size={18} />New {details.singular}</button>
      </section>
      <section className="records-panel" aria-label={details.title}>
        {(kind === 'task' || kind === 'shopping') && records.length > 0 && (
          <div className="filter-row" aria-label={`${details.title} filter`}>
            {(['all', 'open', 'completed'] as const).map((value) => (
              <button type="button" aria-pressed={filter === value} onClick={() => handleFilter(value)} key={value}>
                {value}
              </button>
            ))}
          </div>
        )}
        {sorted.length === 0 ? (
          <EmptyState
            actionLabel={`Create your first ${details.singular}`}
            description={`Your ${details.title.toLowerCase()} are stored only in this browser and device.`}
            icon={details.icon}
            onAction={onNew}
            title={`No ${details.title.toLowerCase()} yet`}
          />
        ) : (
          <div className="records-list-wrapper">
            <div className="records-list">
              {paginated.map((record) => (
                <RecordCard
                  category={categories.find((category) => category.id === record.categoryId)}
                  completedOverride={isCompleted(record)}
                  kind={kind}
                  key={record.id}
                  onDelete={() => onDelete(record)}
                  onEdit={() => onEdit(record)}
                  onToggleChecklist={(itemId) => onToggleChecklist(record, itemId)}
                  onToggleComplete={() => onToggleComplete(record)}
                  onToggleFavorite={() => onToggleFavorite(record)}
                  onTogglePinned={() => onTogglePinned(record)}
                  record={record}
                  timeFormat={timeFormat}
                />
              ))}
            </div>
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </section>
    </div>
  );
};
